import React, { useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppFooter } from "./AppFooter";
import { PwaInstallBanner } from "./PwaInstallBanner";
import { SiteHeader } from "./site/SiteHeader";
import { EspaceSubnav, estPageEspace } from "./site/EspaceSubnav";
import { BandeauEssaiTermine, BandeauFinEssai, BandeauImpaye } from "./site/EtatBandeaux";
import { parisToday } from "../helpers/tirageArticleQuery";
import { initNavTracking } from "../nav/navTrack";
import { trackNavClicked } from "../helpers/analytics";
import { usePublierVisiteur } from "../helpers/usePublierVisiteur";
import styles from "./AppLayout.module.css";

// SYNC : en-tête, barre mobile, pied et fil d'Ariane partagés avec App\Service\MagHeaderRenderer
// (mêmes classes km-*, même catalogue NavCatalog). Le calendrier des tirages reste ici.

export const AppLayout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // html[data-visiteur], comme nav.js : un abonné ne voit aucun rappel d'accès (km.css).
  usePublierVisiteur();

  // Mesure nav_clicked (mêmes attributs data-nav-* que les pages serveur).
  useEffect(() => initNavTracking(document, trackNavClicked), []);

  // Picker tirage : dialog + calendrier vanilla, dates fetch via API (cache 1h).
  // Architecturalement aligné avec MagHeaderRenderer côté PHP (même DOM, même UX).
  const dialogRef = useRef<HTMLDialogElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const stateRef = useRef<{
    available: Set<string> | null;
    minIso: string | null;
    maxIso: string | null;
    curY: number;
    curM: number;
    currentIso: string | null;
  }>({ available: null, minIso: null, maxIso: null, curY: 0, curM: 0, currentIso: null });
  // Rendu d'un mois et chargement des dates, définis une fois avec les écouteurs du calendrier.
  const renderRef = useRef<((y: number, mo: number) => void) | null>(null);
  const loadRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    const dlg = dialogRef.current;
    const labelEl = labelRef.current;
    const grid = gridRef.current;
    const btnPrev = prevRef.current;
    const btnNext = nextRef.current;
    if (!dlg || !labelEl || !grid || !btnPrev || !btnNext) return;
    const months = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
    const pad = (n: number) => (n < 10 ? "0" + n : "" + n);
    const m = location.pathname.match(/^\/tirage\/(\d{4}-\d{2}-\d{2})\//);
    if (m) stateRef.current.currentIso = m[1];

    const render = (y: number, mo: number) => {
      const s = stateRef.current;
      labelEl.textContent = months[mo - 1] + " " + y;
      const monthKey = y + "-" + pad(mo);
      btnPrev.disabled = !!(s.minIso && monthKey <= s.minIso.slice(0, 7));
      btnNext.disabled = !!(s.maxIso && monthKey >= s.maxIso.slice(0, 7));
      const firstDow = (new Date(y, mo - 1, 1).getDay() + 6) % 7;
      const daysInMonth = new Date(y, mo, 0).getDate();
      let html = "";
      for (let i = 0; i < firstDow; i++) html += '<span class="mag-picker__cal-day mag-picker__cal-day--empty" aria-hidden="true"></span>';
      for (let d = 1; d <= daysInMonth; d++) {
        const iso = y + "-" + pad(mo) + "-" + pad(d);
        if (iso === s.currentIso) html += `<span class="mag-picker__cal-day mag-picker__cal-day--active" aria-current="page">${d}</span>`;
        else if (s.available && s.available.has(iso)) html += `<a class="mag-picker__cal-day mag-picker__cal-day--avail" href="/tirage/${iso}/" aria-label="Voir le tirage du ${d} ${months[mo - 1]} ${y}">${d}</a>`;
        else html += `<span class="mag-picker__cal-day mag-picker__cal-day--unavail" aria-disabled="true">${d}</span>`;
      }
      grid.innerHTML = html;
      s.curY = y;
      s.curM = mo;
    };

    const loadDates = (): Promise<void> =>
      fetch("/_api/public/tirage-dates")
        .then((r) => r.json())
        .then((d: { dates?: string[] }) => {
          const arr = d.dates || [];
          const s = stateRef.current;
          s.available = new Set(arr);
          if (arr.length) {
            const sorted = [...arr].sort();
            s.minIso = sorted[0];
            s.maxIso = sorted[sorted.length - 1];
            if (!s.currentIso) s.currentIso = s.maxIso;
          }
        });

    renderRef.current = render;
    loadRef.current = loadDates;

    const onPrev = () => { const s = stateRef.current; let nm = s.curM - 1, ny = s.curY; if (nm < 1) { nm = 12; ny--; } render(ny, nm); };
    const onNext = () => { const s = stateRef.current; let nm = s.curM + 1, ny = s.curY; if (nm > 12) { nm = 1; ny++; } render(ny, nm); };
    const onBackdrop = (e: MouseEvent) => { if (e.target === dlg) dlg.close(); };
    btnPrev.addEventListener("click", onPrev);
    btnNext.addEventListener("click", onNext);
    dlg.addEventListener("click", onBackdrop);
    return () => {
      renderRef.current = null;
      loadRef.current = null;
      btnPrev.removeEventListener("click", onPrev);
      btnNext.removeEventListener("click", onNext);
      dlg.removeEventListener("click", onBackdrop);
    };
  }, []);

  const openPicker = () => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (typeof dlg.showModal !== "function") {
      window.location.assign("/derniers-tirages-keno/");
      return;
    }
    const ouvrir = () => {
      // Le picker a pu être démonté pendant le chargement des dates (navigation entre le clic et
      // la réponse) : abandon silencieux plutôt qu'une erreur (Mobile Safari, Sentry #7597724988).
      if (!renderRef.current || !dialogRef.current) return;
      const base = stateRef.current.currentIso || parisToday();
      renderRef.current(parseInt(base.slice(0, 4), 10), parseInt(base.slice(5, 7), 10));
      dialogRef.current.showModal();
    };
    if (stateRef.current.available === null && loadRef.current) loadRef.current().finally(ouvrir);
    else ouvrir();
  };

  return (
    <div className={styles.layout}>
      <SiteHeader onOpenCalendar={openPicker} />

      {/* Modale picker tirage : partagée avec MagHeaderRenderer côté PHP (même classes CSS) */}
      <dialog id="mag-tirage-picker" className="mag-picker" ref={dialogRef}>
        <form method="dialog" className="mag-picker__inner">
          <header className="mag-picker__header">
            <p className="mag-picker__title">Choisir un tirage</p>
            <button type="submit" className="mag-picker__close" aria-label="Fermer">✕</button>
          </header>
          <div className="mag-picker__cal">
            <div className="mag-picker__cal-nav">
              <button type="button" className="mag-picker__cal-prev" aria-label="Mois précédent" ref={prevRef}>‹</button>
              <span className="mag-picker__cal-label" aria-live="polite" ref={labelRef}></span>
              <button type="button" className="mag-picker__cal-next" aria-label="Mois suivant" ref={nextRef}>›</button>
            </div>
            <div className="mag-picker__cal-weekdays">
              <span>L</span><span>M</span><span>M</span><span>J</span><span>V</span><span>S</span><span>D</span>
            </div>
            <div className="mag-picker__cal-grid" role="grid" ref={gridRef}></div>
          </div>
          <footer className="mag-picker__footer">
            <a className="mag-picker__all" href="/derniers-tirages-keno/">Voir tous les tirages →</a>
          </footer>
        </form>
      </dialog>

      <main className={styles.main} id="contenu">
        {/* D8 : impayé signalé sur toutes les pages ; D10 : fin d'essai sur le tableau de bord. */}
        <BandeauImpaye />
        {location.pathname.startsWith("/home") && <BandeauFinEssai />}
        <BandeauEssaiTermine />
        {/* Grande feuille blanche commune (même gabarit que .km-sheet des pages serveur, décision du 26/10/2026). */}
        <div className={styles.feuille}>
          {/* Fil d'Ariane et onglets de Mon espace dans la feuille, comme le fil d'Ariane des autres pages. */}
          {estPageEspace(location.pathname) && <EspaceSubnav />}
          {children}
        </div>
      </main>

      <AppFooter />
      <PwaInstallBanner />
    </div>
  );
};
